import { FC, Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { LoyaltyPointsVersionsWrapper } from './LoyaltyPointsVersionsWrapper';
import { Loading } from 'components/Loading';
import { addQuery, QueryRoute, updateQuery } from 'components/QueryRoute';
import { getSectionsData } from 'store/selectors/app-data';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';
import { AccountsLayout } from '../layout';

/* prettier-ignore-start */ /* eslint-disable */
const { Login } =                     lazy(() => import(/* webpackChunkName: "login" */                        './Login'));
const { Wallet } =                    lazy(() => import(/* webpackChunkName: "wallet" */                       './Wallet'));
const { ProfilePreview } =            lazy(() => import(/* webpackChunkName: "profile" */                      './Profile'));
const { Register } =                  lazy(() => import(/* webpackChunkName: "register" */                     './Register'));
const { Messages } =                  lazy(() => import(/* webpackChunkName: "account-messages" */             './Messages'));
const { NewPassword } =               lazy(() => import(/* webpackChunkName: "new-password" */                 './NewPassword'));
const { BonusIframe } =               lazy(() => import(/* webpackChunkName: "bonus-iframe" */                 './BonusIframe'));
const { BonusesMenu } =               lazy(() => import(/* webpackChunkName: "bonuses-menu" */                 './Bonuses/Menu'));
const { Settings } =                  lazy(() => import(/* webpackChunkName: "settings" */                     './Settings/Menu'));
const { MyCards } =                   lazy(() => import(/* webpackChunkName: "my-cards" */                     './MyCards/index'));
const { Bonuses } =                   lazy(() => import(/* webpackChunkName: "bonuses" */                      './Bonuses/Bonus'));
const { Rewards } =                   lazy(() => import(/* webpackChunkName: "rewards" */                      './Rewards'));
const { ForgotPassword } =            lazy(() => import(/* webpackChunkName: "forgot-password" */              './Forgot/Password'));
const { BalanceHistoryPage } =        lazy(() => import(/* webpackChunkName: "account-balance" */              './AccountBalance'));
const { AccountStakeAndEarn } =       lazy(() => import(/* webpackChunkName: "account-stake-and-earb" */       './AccountStakeAndEarn'));
const { Limits } =                    lazy(() => import(/* webpackChunkName: "settings-limits" */              './Settings/Limits'));
const { Statement } =                 lazy(() => import(/* webpackChunkName: "statment" */                     './Statement/index'));
const { CustomerJourneyLayout } =     lazy(() => import(/* webpackChunkName: "customer-journey" */             './CustomerJourney'));
const { TimeOut } =                   lazy(() => import(/* webpackChunkName: "settings-timeout" */             './Settings/TimeOut'));
const { GamStop } =                   lazy(() => import(/* webpackChunkName: "settings-gamstop" */             './Settings/GamStop'));
const { ReferFriend } =               lazy(() => import(/* webpackChunkName: "refer-a-friend" */               './Bonuses/ReferFriend'));
const { LoginLimit } =                lazy(() => import(/* webpackChunkName: "settings-login-limit" */         './Settings/LoginLimit'));
const { BetingHistoryLayout } =       lazy(() => import(/* webpackChunkName: "betting-history" */              './BetingHistory/index'));
const { RealityCheck } =              lazy(() => import(/* webpackChunkName: "settings-reality-check" */       './Settings/RealityCheck'));
const { ProfileEdit } =               lazy(() => import(/* webpackChunkName: "settings-profile" */             './Settings/EditeProfile'));
const { SelfExclusion } =             lazy(() => import(/* webpackChunkName: "settings-self-exclusion" */      './Settings/SelfExclusion'));
const { NotificationSettings } =      lazy(() => import(/* webpackChunkName: "settings-notifications" */       './Settings/Notifications'));
const { AccountStatus } =             lazy(() => import(/* webpackChunkName: "account-status" */                './Settings/AccountStatus'));
const { ChangePassword } =            lazy(() => import(/* webpackChunkName: "settings-change-pass" */         './Settings/ChangePassword'));
const { MySessions } =                lazy(() => import(/* webpackChunkName: "settings-my-session" */          './Settings/Sessions/index'));
const { BonusManagement } =           lazy(() => import(/* webpackChunkName: "bonus-management" */             './Bonuses/BonusManagement'));
const { Documents } =                 lazy(() => import(/* webpackChunkName: "settings-documents" */           './Settings/Documents/index'));
const { AccumulatedProfit } =         lazy(() => import(/* webpackChunkName: "settings-profit" */              './Settings/AccumulatedProfit'));
const { VerifyAccount } =             lazy(() => import(/* webpackChunkName: "settings-verify-account" */      './Settings/VerifyAccount/index'));
const { TwoFactorAuth } =             lazy(() => import(/* webpackChunkName: "settings-two-factor" */          './Settings/TwoFactorAuth/index'));
const { SaferGambling } =             lazy(() => import(/* webpackChunkName: "settings-safer-gambling" */      './Settings/SaferGambling/index'));
const { CurrentSession } =            lazy(() => import(/* webpackChunkName: "settings-current-session" */     './Settings/SessionInformation/index'));
const { AvailabilityPeriod } =        lazy(() => import(/* webpackChunkName: "settings-availability-period" */ './Settings/AvailabilityPeriod/index'));
const { ForgotUsername } =            lazy(() => import(/* webpackChunkName: "forgot-username" */              './Forgot/Username'));
const { BankAccount } =               lazy(() => import(/* webpackChunkName: "bank-account" */                 './BankAccount'));
const { TransferToFriend } =          lazy(() => import(/* webpackChunkName: "transfer-to-friend-section" */   './TransferToFriend/ViewWithVerifyChecker'));
const { VerificationSettings } =      lazy(() => import(/* webpackChunkName: "Verification-Settings" */        './Settings/VerificationSettings'));
const { MyNfts } =                 lazy(() => import(/* webpackChunkName: "owned-nfts" */                   'elements/Accounts/views/MyNfts'));

/* prettier-ignore-end */ /* eslint-enable */

export const Accounts: FC<Record<string, any>> = props => {
  const history = useHistory();
  const user = useSelector(getUser);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const accountSections = useSelector(getSectionsData);

  const checkIsSectionVisible = (sectionType: string): boolean => {
    if (accountSections?.myProfile) {
      return !accountSections.myProfile.find(
        section => section.sectionType === sectionType
      )?.hidden;
    }

    return false;
  };

  const redirectToProfile = useCallback(() => {
    history.push(updateQuery({ accounts: '*', settings: '*', profile: '*' }));
  }, []);

  if (user.pending || (isLoggedIn && !accountSections?.myProfile)) {
    return null;
  }

  return (
    <AccountsLayout>
      <Suspense fallback={<Loading />}>
        <QueryRoute
          includes={['accounts', 'login']}
          shouldMount={() => !isLoggedIn && props.signInBtn}
          onCatch={() =>
            history.push(addQuery({ accounts: undefined, login: undefined }))
          }
          excludes={[]}
          render={() => (
            <Login
              accountNextButtonStyle={props.accountNextButtonStyle}
              signInBtn={props.signInBtn}
              signUpBtn={props.signUpBtn}
            />
          )}
        />

        <QueryRoute
          includes={['accounts', 'forgot-password']}
          excludes={[]}
          component={ForgotPassword}
        />
        <QueryRoute
          includes={['accounts', 'forgot-username']}
          excludes={[]}
          component={ForgotUsername}
        />
        <QueryRoute
          includes={['accounts', 'reset-password']}
          excludes={[]}
          component={NewPassword}
        />
        <QueryRoute
          includes={['accounts', 'register']}
          excludes={[]}
          shouldMount={() => !isLoggedIn && props.signUpBtn}
          onCatch={() =>
            history.push(
              updateQuery({ accounts: undefined, register: undefined })
            )
          }
          render={() => (
            <Register
              setVisible={props.setVisible}
              accountSignUpButtonStyle={props.accountSignUpButtonStyle}
              accountNextButtonStyle={props.accountNextButtonStyle}
              accountPrevButtonStyle={props.accountPrevButtonStyle}
              signInBtn={props.signInBtn}
              signUpBtn={props.signUpBtn}
              inPopup={props.inPopup}
            />
          )}
        />

        <QueryRoute
          includes={['accounts', 'profile']}
          excludes={['settings']}
          component={ProfilePreview}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'profile']}
          excludes={[]}
          component={ProfileEdit}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'menu']}
          excludes={[]}
          component={Settings}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'password']}
          excludes={[]}
          component={ChangePassword}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'limits']}
          excludes={[]}
          component={Limits}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabBetLimit')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'bonuses', 'menu']}
          excludes={[]}
          component={BonusesMenu}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'bonuses', 'bonus']}
          excludes={[]}
          component={Bonuses}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'bonuses', 'refer-friend']}
          excludes={[]}
          component={ReferFriend}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'bonuses', 'management']}
          excludes={[]}
          component={BonusManagement}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'bonus-iframe']}
          excludes={[]}
          component={BonusIframe}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'loyalty-points']}
          excludes={[]}
          component={LoyaltyPointsVersionsWrapper}
          auth={true}
        />

        <QueryRoute
          includes={[
            ['accounts', 'rewards'],
            ['accounts', 'nftRewards']
          ]}
          excludes={[]}
          component={Rewards}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'gamstop']}
          excludes={[]}
          component={GamStop}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabGamstop')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'accumulated-profit']}
          excludes={[]}
          component={AccumulatedProfit}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'reality-check']}
          excludes={[]}
          component={RealityCheck}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'login-limit']}
          excludes={[]}
          component={LoginLimit}
          auth={true}
          shouldMount={() => checkIsSectionVisible('loginLimit')}
          onCatch={redirectToProfile}
        />
        {SpringConfigs.SHOW_ACCOUNT_SESSION && (
          <QueryRoute
            includes={['accounts', 'settings', 'session-information']}
            excludes={[]}
            component={CurrentSession}
            auth={true}
          />
        )}
        <QueryRoute
          includes={['accounts', 'settings', 'safer-gambling']}
          excludes={[]}
          component={SaferGambling}
          auth={true}
          shouldMount={() => checkIsSectionVisible('saferGambling')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'availability-period']}
          excludes={[]}
          component={AvailabilityPeriod}
          auth={true}
          shouldMount={() => checkIsSectionVisible('availability-period')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'sessions']}
          excludes={[]}
          component={MySessions}
          auth={true}
          shouldMount={() => checkIsSectionVisible('current-session-info')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'notification-settings']}
          excludes={[]}
          component={NotificationSettings}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabNotifications')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'timeout']}
          excludes={[]}
          component={TimeOut}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabTimeOut')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'account-status']}
          excludes={[]}
          component={AccountStatus}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'self-exclusion']}
          excludes={[]}
          component={SelfExclusion}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabSelfExclusion')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'documents']}
          excludes={[]}
          component={Documents}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabDocuments')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'verify-account']}
          excludes={[]}
          component={VerifyAccount}
          auth={true}
          shouldMount={() => checkIsSectionVisible('tabVerifyAccount')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'settings', 'verification-settings']}
          excludes={[]}
          component={VerificationSettings}
          auth={true}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'bet-history']}
          excludes={[]}
          component={BetingHistoryLayout}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'stake-and-earn']}
          excludes={[]}
          component={!isMobile() && AccountStakeAndEarn}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'balance-history']}
          excludes={[]}
          component={BalanceHistoryPage}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'my-cards']}
          excludes={[]}
          component={MyCards}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'statement']}
          excludes={[]}
          component={Statement}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'two-factor-auth']}
          excludes={[]}
          component={TwoFactorAuth}
          auth={true}
          shouldMount={() => checkIsSectionVisible('twoFactorAuthentication')}
          onCatch={redirectToProfile}
        />
        <QueryRoute
          includes={['accounts', 'wallet']}
          excludes={[]}
          auth={true}
          render={(routeProps: any) => {
            return <Wallet key={routeProps.location.search} />;
          }}
        />
        <QueryRoute
          includes={['accounts', 'messages']}
          excludes={[]}
          component={Messages}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'journey']}
          excludes={[]}
          component={CustomerJourneyLayout}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'bank-account']}
          excludes={[]}
          component={BankAccount}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'transfer-to-friend']}
          excludes={[]}
          component={TransferToFriend}
          auth={true}
        />
        <QueryRoute
          includes={['accounts', 'my-nfts']}
          excludes={[]}
          component={MyNfts}
          auth={true}
        />
      </Suspense>
    </AccountsLayout>
  );
};
